import { useEffect, useState } from 'react';

import { adminClient, type AdminLegalAcceptancesResponse } from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { DataTable, DataTableCell, DataTableHead, DataTableLoadError, DataTableMessage, DataTableRow } from '@/components/table/DataTable';

const COLUMNS = [{ label: 'Person' }, { label: 'Terms' }, { label: 'Privacy' }, { label: 'Accepted' }];

function formatAcceptedAt(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

/** Read-only list of who in this organization accepted which Terms of Use and Privacy Policy versions. */
export function OrgLegalAcceptances({ orgId }: { orgId: string }) {
  const [data, setData] = useState<AdminLegalAcceptancesResponse | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setData(undefined);
    setError(undefined);
    adminClient
      .listLegalAcceptances(orgId)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((reason: unknown) => {
        if (!cancelled) setError(reason instanceof Error ? reason.message : 'Failed to load terms acceptances');
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, reloadKey]);

  const items = data?.items ?? [];

  return (
    <section className="mt-8">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-foreground">Terms acceptance</h2>
          <p className="text-xs text-muted-foreground">
            Who accepted which Terms of Use and Privacy Policy version, newest first.
          </p>
        </div>
        {data ? (
          <StatusPill tone={data.enforced ? 'success' : 'neutral'}>
            {data.enforced ? 'Acceptance required' : 'Acceptance not required'}
          </StatusPill>
        ) : null}
      </div>
      <DataTable>
        <DataTableHead columns={COLUMNS} />
        {error ? (
          <DataTableLoadError colSpan={COLUMNS.length} message={error} onRetry={() => setReloadKey((key) => key + 1)} />
        ) : !data ? (
          <DataTableMessage colSpan={COLUMNS.length} loading>
            Loading terms acceptances
          </DataTableMessage>
        ) : items.length === 0 ? (
          <DataTableMessage colSpan={COLUMNS.length}>No one in this organization has accepted yet.</DataTableMessage>
        ) : (
          <tbody>
            {items.map((item) => (
              <DataTableRow key={`${item.userId}:${item.termsVersion}:${item.privacyVersion}`}>
                <DataTableCell>
                  <p className="truncate font-medium text-foreground">{item.email || item.userId}</p>
                  {item.email ? <p className="truncate text-xs text-muted-foreground">{item.userId}</p> : null}
                </DataTableCell>
                <DataTableCell className="font-mono text-xs">{item.termsVersion}</DataTableCell>
                <DataTableCell className="font-mono text-xs">{item.privacyVersion}</DataTableCell>
                <DataTableCell className="text-xs text-muted-foreground">
                  {formatAcceptedAt(item.acceptedAt)}
                  {item.ip ? <span className="block">{item.ip}</span> : null}
                </DataTableCell>
              </DataTableRow>
            ))}
          </tbody>
        )}
      </DataTable>
    </section>
  );
}
